import { collection, getDocs, query, where } from "firebase/firestore";
import { geohashQueryBounds } from "geofire-common";
import { studioDb } from "../firebase";

const DEFAULT_OFFERS_RADIUS_METERS = 7500;
const MAX_OFFERS = 40;

function toFiniteNumber(value) {
  if (value == null || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function isValidCenter(center) {
  const lat = toFiniteNumber(center?.lat);
  const lng = toFiniteNumber(center?.lng);
  return lat != null && lng != null && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

function distanceMetersBetween(a, b) {
  const lat1 = toFiniteNumber(a?.lat);
  const lng1 = toFiniteNumber(a?.lng);
  const lat2 = toFiniteNumber(b?.lat);
  const lng2 = toFiniteNumber(b?.lng);
  if (lat1 == null || lng1 == null || lat2 == null || lng2 == null) {
    return Number.POSITIVE_INFINITY;
  }
  const toRad = (degrees) => (degrees * Math.PI) / 180;
  const earthRadiusMeters = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const rLat1 = toRad(lat1);
  const rLat2 = toRad(lat2);
  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(rLat1) * Math.cos(rLat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return earthRadiusMeters * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function toTimeMs(value) {
  if (!value) return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  const parsed = new Date(value);
  const timeMs = parsed.getTime();
  return Number.isFinite(timeMs) ? timeMs : 0;
}

function normalizeCampaignType(value = "") {
  return String(value || "").trim().toLowerCase() === "coupon" ? "coupon" : "raffle";
}

function isStableClaimPath(path = "") {
  return /^\/(?:coupons|raffles)\/c\/[^/]+$/i.test(String(path || "").trim());
}

export function getCampaignOfferClaimPath(offer = {}) {
  const stablePath = String(offer?.stableClaimPath || "").trim();
  if (isStableClaimPath(stablePath)) return stablePath;
  const publicPath = String(offer?.publicClaimPath || "").trim();
  if (isStableClaimPath(publicPath)) return publicPath;
  const campaignId = String(offer?.campaignId || offer?.id || "").trim();
  if (!campaignId) return "";
  return normalizeCampaignType(offer?.campaignType) === "coupon"
    ? `/coupons/c/${campaignId}`
    : `/raffles/c/${campaignId}`;
}

function isActiveOffer(row = {}, nowMs = Date.now()) {
  const status = String(row?.status || "").trim().toLowerCase();
  if (status && status !== "published") return false;
  const startMs = toTimeMs(row?.claimStartAt);
  const endMs = toTimeMs(row?.claimEndAt);
  if (startMs > 0 && nowMs < startMs) return false;
  if (endMs > 0 && nowMs > endMs) return false;
  return true;
}

function sanitizeOffer(row = {}, extra = {}) {
  const lat = toFiniteNumber(row?.lat);
  const lng = toFiniteNumber(row?.lng);
  const campaignType = normalizeCampaignType(row?.campaignType);
  const claimPath = getCampaignOfferClaimPath({ ...row, campaignType });
  return {
    id: String(row?.campaignId || row?.id || "").trim(),
    campaignId: String(row?.campaignId || row?.id || "").trim(),
    campaignType,
    businessId: String(row?.businessId || "").trim(),
    title: String(row?.title || "").trim(),
    slug: String(row?.slug || "").trim(),
    heroImageUrl: String(row?.heroImageUrl || "").trim(),
    claimStartAt: row?.claimStartAt || null,
    claimEndAt: row?.claimEndAt || null,
    locationId: String(row?.locationId || "").trim(),
    locationName: String(row?.locationName || "").trim(),
    address: String(row?.address || "").trim(),
    lat,
    lng,
    geohash: String(row?.geohash || "").trim(),
    publicClaimPath: claimPath,
    publicClaimUrl: String(row?.publicClaimUrl || "").trim(),
    distanceMeters: Number.isFinite(extra.distanceMeters) ? extra.distanceMeters : null,
  };
}

function dedupeOffers(rows = []) {
  const byCampaign = new Map();
  rows.forEach((row) => {
    const key = String(row?.campaignId || row?.id || "").trim();
    if (!key) return;
    const existing = byCampaign.get(key);
    if (!existing || Number(row?.distanceMeters || Infinity) < Number(existing?.distanceMeters || Infinity)) {
      byCampaign.set(key, row);
    }
  });
  return Array.from(byCampaign.values());
}

function warnOfferDistanceMismatch(data = {}, center = {}, distanceMeters = 0, radiusMeters = 0) {
  if (process.env.NODE_ENV === "production") return;
  const title = String(data?.title || data?.campaignId || data?.id || "offer").trim();
  console.warn("[offers] filtered distant offer", {
    title,
    campaignId: data?.campaignId || data?.id || "",
    geohash: data?.geohash || "",
    center,
    offerLocation: { lat: data?.lat, lng: data?.lng },
    distanceKm: Number.isFinite(distanceMeters) ? Number((distanceMeters / 1000).toFixed(2)) : null,
    radiusKm: Number((radiusMeters / 1000).toFixed(2)),
  });
}

export async function fetchNearbyCampaignOffers({
  center,
  radius = DEFAULT_OFFERS_RADIUS_METERS,
  limit = MAX_OFFERS,
} = {}) {
  if (!studioDb || !isValidCenter(center)) return [];
  const finalRadius = Math.max(500, Math.min(50000, Number(radius) || DEFAULT_OFFERS_RADIUS_METERS));
  const bounds = geohashQueryBounds([Number(center.lat), Number(center.lng)], finalRadius);
  const snapshots = await Promise.all(
    bounds.map(([start, end]) =>
      getDocs(
        query(
          collection(studioDb, "raffleLocationIndex"),
          where("status", "==", "published"),
          where("geohash", ">=", start),
          where("geohash", "<=", end)
        )
      )
    )
  );
  const nowMs = Date.now();
  const rows = [];
  snapshots.forEach((snapshot) => {
    snapshot.docs.forEach((docSnap) => {
      const data = { id: docSnap.id, ...docSnap.data() };
      if (!isActiveOffer(data, nowMs)) return;
      const lat = toFiniteNumber(data.lat);
      const lng = toFiniteNumber(data.lng);
      if (lat == null || lng == null) return;
      const distanceMeters = distanceMetersBetween(center, { lat, lng });
      if (distanceMeters > finalRadius) {
        warnOfferDistanceMismatch(data, center, distanceMeters, finalRadius);
        return;
      }
      rows.push(sanitizeOffer(data, { distanceMeters }));
    });
  });
  return dedupeOffers(rows)
    .sort((a, b) => Number(a.distanceMeters || Infinity) - Number(b.distanceMeters || Infinity))
    .slice(0, Math.max(1, Number(limit) || MAX_OFFERS));
}

export async function fetchCampaignOffersForBusinesses(businessIds = [], { limit = MAX_OFFERS } = {}) {
  if (!studioDb) return [];
  const ids = Array.from(new Set((Array.isArray(businessIds) ? businessIds : []).map((id) => String(id || "").trim()).filter(Boolean)));
  if (!ids.length) return [];
  const chunks = [];
  for (let i = 0; i < ids.length; i += 10) chunks.push(ids.slice(i, i + 10));

  const snapshots = await Promise.all(
    chunks.map((chunk) =>
      getDocs(
        query(
          collection(studioDb, "raffleLocationIndex"),
          where("businessId", "in", chunk),
          where("status", "==", "published")
        )
      )
    )
  );
  const nowMs = Date.now();
  const rows = [];
  snapshots.forEach((snapshot) => {
    snapshot.docs.forEach((docSnap) => {
      const data = { id: docSnap.id, ...docSnap.data() };
      if (!isActiveOffer(data, nowMs)) return;
      rows.push(sanitizeOffer(data));
    });
  });
  return dedupeOffers(rows)
    .sort((a, b) => toTimeMs(a.claimEndAt) - toTimeMs(b.claimEndAt))
    .slice(0, Math.max(1, Number(limit) || MAX_OFFERS));
}
